import type { DraftDetail, ErrorStage, JournalDraftStatus } from './types';

export const TERMINAL_STATUSES: JournalDraftStatus[] = [
	'POSTED',
	'UNSUPPORTED_INPUT',
	'INVALID_ENTRY',
	'DECLINED_BY_USER',
	'QBO_POSTING_FAILED',
	'AI_PROVIDER_UNAVAILABLE',
];

// States that age out after 30 days (contract decision 9) — expiry is shown for these.
export const EXPIRING_STATUSES: JournalDraftStatus[] = [
	'DRAFT_READY',
	'IN_REVIEW',
	'NEEDS_CLARIFICATION',
];

export const isTerminalStatus = (status: JournalDraftStatus) =>
	TERMINAL_STATUSES.includes(status);

export const STATUS_META: Record<
	JournalDraftStatus,
	{ label: string; tone: 'neutral' | 'info' | 'warning' | 'success' | 'danger' }
> = {
	RECEIVED: { label: 'Received', tone: 'neutral' },
	EXTRACTING: { label: 'Building draft', tone: 'info' },
	NEEDS_CLARIFICATION: { label: 'Needs clarification', tone: 'warning' },
	DRAFT_READY: { label: 'Draft ready', tone: 'info' },
	IN_REVIEW: { label: 'In review', tone: 'info' },
	APPROVED: { label: 'Approved', tone: 'info' },
	POSTING_TO_QBO: { label: 'Posting to QuickBooks', tone: 'info' },
	POSTED: { label: 'Posted', tone: 'success' },
	UNSUPPORTED_INPUT: { label: 'Unsupported input', tone: 'danger' },
	INVALID_ENTRY: { label: 'Invalid entry', tone: 'danger' },
	DECLINED_BY_USER: { label: 'Declined', tone: 'neutral' },
	QBO_POSTING_FAILED: { label: 'QuickBooks posting failed', tone: 'danger' },
	AI_PROVIDER_UNAVAILABLE: { label: 'AI unavailable', tone: 'danger' },
	EXPIRED: { label: 'Expired', tone: 'warning' },
};

export const ALL_STATUSES = Object.keys(STATUS_META) as JournalDraftStatus[];

export const STAGE_LABEL: Record<ErrorStage, string> = {
	extraction: 'Extraction',
	validation: 'Validation',
	approval: 'Approval',
	qbo_posting: 'QuickBooks posting',
};

// Money is carried as decimal strings ("1234.50"); format without a float
// round-trip so what's shown is exactly what the backend validated.
export const formatMoney = (value: string | null | undefined) => {
	if (value === null || value === undefined || value === '') {
		return '';
	}
	const trimmed = String(value).trim();
	const negative = trimmed.startsWith('-');
	const [wholeRaw, fracRaw = ''] = trimmed.replace(/^[-+]/, '').split('.');
	const whole = (wholeRaw || '0').replace(/^0+(?=\d)/, '');
	const frac = (fracRaw + '00').slice(0, 2);
	const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
	return `${negative ? '-' : ''}$${grouped}.${frac}`;
};

// Integer cents from a decimal string — used only to show an out-of-balance
// difference; the balanced verdict itself comes from the backend.
export const toCents = (value: string | null | undefined) => {
	if (!value) {
		return 0;
	}
	const trimmed = String(value).trim();
	const negative = trimmed.startsWith('-');
	const [whole = '0', frac = ''] = trimmed.replace(/^[-+]/, '').split('.');
	const cents = Number(whole || '0') * 100 + Number((frac + '00').slice(0, 2));
	return negative ? -cents : cents;
};

export const centsToString = (cents: number) => {
	const negative = cents < 0;
	const abs = Math.abs(cents);
	return `${negative ? '-' : ''}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
};

// YYYY-MM-DD → M/D/YYYY without a Date round-trip (avoids timezone day shifts).
export const formatTxnDate = (value: string | null | undefined) => {
	if (!value) {
		return '';
	}
	const [year, month, day] = value.slice(0, 10).split('-');
	return year && month && day ? `${Number(month)}/${Number(day)}/${year}` : value;
};

export const formatTimestamp = (value: string | null | undefined) => {
	if (!value) {
		return '';
	}
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return value;
	}
	return date.toLocaleString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
	});
};

const qboMode = process.env.REACT_APP_QBO_MODE || 'sandbox';

// Same deep link the Journal Entries table uses; PostResult.qboUrl wins when present.
export const qboJournalUrl = (txnId: string | null | undefined) =>
	txnId
		? `https://app${qboMode !== 'prod' ? '.sandbox' : ''}.qbo.intuit.com/app/journal?txnId=${txnId}`
		: null;

export const accountsTouched = (draft: DraftDetail) =>
	Array.from(new Set(draft.lines.map((l) => l.accountName).filter(Boolean)));
